import { z } from 'zod'
import type { PullRequest } from '../data/projectTypes'
import { previewWait } from './previewDeployment'

const GITHUB_TEST_RUN_PREFIX = 'github-pr-'

export const TestRunOrderItemSchema = z.object({
  kind: z.enum(['session', 'github-pr']),
  session_id: z.string().nullable(),
  version_id: z.string().nullable(),
})

export type TestRunOrderItem = z.infer<typeof TestRunOrderItemSchema>

/** The frontend-only id used for a PR that has no persisted test session yet. */
export function githubTestRunId(versionId: string): string {
  return `${GITHUB_TEST_RUN_PREFIX}${encodeURIComponent(versionId)}`
}

/**
 * The Test Run detail route for a PR's version. Also used by the GitHub bot (netlify/functions/
 * lib/github-app/report.ts) so its PR comments and Check Run "Details" links land on the run itself
 * rather than the project's whole GitHub tab. Resolves to the persisted test session's detail page
 * once one exists — TestRunSessionDetail matches this id by version too.
 */
export function githubTestRunPath(projectId: string, versionId: string): string {
  return `/projects/${encodeURIComponent(projectId)}/test-runs/${githubTestRunId(versionId)}`
}

/** Resolve a frontend-only PR row back to the version it represents. */
export function githubVersionIdFromTestRunId(testRunId: string): string | null {
  if (!testRunId.startsWith(GITHUB_TEST_RUN_PREFIX)) return null
  try {
    return decodeURIComponent(testRunId.slice(GITHUB_TEST_RUN_PREFIX.length))
  } catch {
    return null
  }
}

/**
 * The body `GET /api/github-prs` answers with when a version has no GitHub PR
 * (netlify/functions/github-prs.ts). It is that version's final answer — a retry asks the same
 * question about the same row and gets the same 404.
 */
const GithubPullRequestNotFoundResponseSchema = z.object({ error: z.literal('Pull request not found') })

/**
 * True when a `getProjectPullRequest` query failed with that authoritative 404. The test-run page
 * uses it to stop interval-polling the lookup, which would otherwise re-send an identical doomed
 * request every cycle (QA recorded 12 failing GETs in 110s from a fixed 10s poll).
 */
export function isGithubPrNotFound(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const { status, data } = error as { status?: unknown; data?: unknown }
  if (status !== 404) return false
  return GithubPullRequestNotFoundResponseSchema.safeParse(data).success
}

/** Keep GitHub's approval vocabulary aligned with the Test Run status vocabulary. */
export function githubApprovalToRunStatus(approval: PullRequest['approval']): string {
  switch (approval) {
    case 'approved': return 'completed'
    case 'blocked': return 'failed'
    case 'pending': return 'in-progress'
    case 'cancelled': return 'incomplete'
  }
}

/**
 * The Test Run status to show for a PR revision. A revision whose preview deployment has not been
 * detected is reported as such rather than as 'in-progress': its approval is 'pending' from the moment
 * the commit lands, so the approval alone would claim QA is running against a build nobody has found.
 */
export function githubPrRunStatus(pr: PullRequest): string {
  if (previewWait(pr)) return 'awaiting-deployment'
  return githubApprovalToRunStatus(pr.approval)
}

export function pullRequestSearchText(pr: PullRequest): string {
  return [
    `pr #${pr.pr_number}`,
    pr.branch_name,
    pr.head_sha,
    pr.repo_full_name,
    pr.approval,
  ].join(' ').toLowerCase()
}
